import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Search, Check, RotateCcw, CircleDot, Percent, Coins, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { adminMarketList, adminMarketApply, apiErr } from "@/lib/api";
import { formatPrice } from "@/lib/site";

const istDay = (iso) => {
  if (!iso) return null;
  try {
    return new Date(new Date(iso).getTime() + 3 * 3600000).toISOString().slice(0, 10);
  } catch {
    return null;
  }
};
const todayIst = () => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 10);
const round5 = (n) => Math.round(n / 5) * 5;
const PAGE = 60;

const BULK_MODES = [
  { value: "pct_up", label: "Yüzde Zam (%)", price: true },
  { value: "pct_down", label: "Yüzde İndirim (%)", price: true },
  { value: "tl_up", label: "TL Artış (+₺)", price: true },
  { value: "tl_down", label: "TL Azalış (−₺)", price: true },
  { value: "set_active", label: "Satışta Yap", price: false },
  { value: "set_passive", label: "Pasif Yap", price: false },
];

export default function MarketPriceManager() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [visible, setVisible] = useState(PAGE);
  const [roundOn, setRoundOn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState("pct_up");
  const [bulkValue, setBulkValue] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [bulkOps, setBulkOps] = useState([]);
  const [inlineTouched, setInlineTouched] = useState(false);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["admin", "market"],
    queryFn: adminMarketList,
  });

  const today = todayIst();
  const bySlug = useMemo(() => Object.fromEntries(products.map((p) => [p.slug, p])), [products]);
  const groups = useMemo(() => {
    const seen = new Map();
    products.forEach((p) => { if (!seen.has(p.group_key)) seen.set(p.group_key, p.group_label); });
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [products]);

  const priceVal = (slug) =>
    edits[slug]?.price !== undefined ? edits[slug].price : String(bySlug[slug]?.price ?? "");
  const statusVal = (slug) =>
    edits[slug]?.status !== undefined ? edits[slug].status : bySlug[slug]?.status || "available";
  const effPrice = (slug) => {
    const v = edits[slug]?.price !== undefined ? Number(edits[slug].price) : Number(bySlug[slug]?.price);
    return Number.isFinite(v) ? v : 0;
  };

  const isPriceDirty = (slug) =>
    edits[slug]?.price !== undefined && Number(edits[slug].price) !== Number(bySlug[slug]?.price);
  const isStatusDirty = (slug) =>
    edits[slug]?.status !== undefined && edits[slug].status !== (bySlug[slug]?.status || "available");
  const isDirty = (slug) => isPriceDirty(slug) || isStatusDirty(slug);

  const dirtyList = useMemo(
    () => products.filter((p) => isDirty(p.slug)),
    [products, edits] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return products.filter((p) => {
      if (group !== "all" && p.group_key !== group) return false;
      if (q && !p.name.toLocaleLowerCase("tr").includes(q) &&
          !(p.subcategory || "").toLocaleLowerCase("tr").includes(q)) return false;
      return true;
    });
  }, [products, group, search]);

  const visibleRows = filtered.slice(0, visible);
  const updatedToday = products.filter((p) => istDay(p.price_updated_at) === today).length;

  const setPrice = (slug, v) => {
    setInlineTouched(true);
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], price: v.replace(",", ".") } }));
  };
  const blurPrice = (slug) => {
    const raw = edits[slug]?.price;
    if (raw === undefined || raw === "") return;
    let n = Number(raw);
    if (!Number.isFinite(n)) return;
    n = Math.max(0, Math.round(n));
    if (roundOn) n = round5(n);
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], price: String(n) } }));
  };
  const setStatus = (slug, on) => {
    setInlineTouched(true);
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], status: on ? "available" : "passive" } }));
  };

  const reset = () => { setEdits({}); setBulkOps([]); setInlineTouched(false); };

  const applyBulk = () => {
    const mode = BULK_MODES.find((m) => m.value === bulkMode);
    const v = Number(bulkValue);
    if (mode.price && (!Number.isFinite(v) || v <= 0)) {
      toast.error("Geçerli bir değer girin");
      return;
    }
    setEdits((e) => {
      const next = { ...e };
      filtered.forEach((p) => {
        if (!mode.price) {
          next[p.slug] = { ...next[p.slug], status: bulkMode === "set_active" ? "available" : "passive" };
          return;
        }
        let np = effPrice(p.slug);
        if (bulkMode === "pct_up") np *= 1 + v / 100;
        else if (bulkMode === "pct_down") np *= 1 - v / 100;
        else if (bulkMode === "tl_up") np += v;
        else if (bulkMode === "tl_down") np -= v;
        np = Math.max(0, Math.round(np));
        if (roundOn) np = round5(np);
        next[p.slug] = { ...next[p.slug], price: String(np) };
      });
      return next;
    });
    setBulkOps((o) => [...o, {
      type: bulkMode,
      group_label: group === "all" ? "Tüm Gruplar" : (groups.find((g) => g.key === group)?.label || "Çeşitli"),
      value: mode.price ? v : null,
    }]);
    toast.success(`${filtered.length} ürüne uygulandı — kaydetmeden önce inceleyebilirsiniz`);
  };

  const save = async () => {
    const items = dirtyList.map((p) => {
      const base = bySlug[p.slug];
      const it = {
        slug: p.slug, name: p.name,
        old_price: base.price, new_price: base.price,
        old_status: base.status || "available", new_status: base.status || "available",
      };
      if (isPriceDirty(p.slug)) it.new_price = Number(edits[p.slug].price);
      if (isStatusDirty(p.slug)) it.new_status = edits[p.slug].status;
      return it;
    });
    let op_type = "inline", group_label = "Çeşitli", value = null;
    if (bulkOps.length === 1 && !inlineTouched) {
      op_type = `bulk_${bulkOps[0].type}`;
      group_label = bulkOps[0].group_label;
      value = bulkOps[0].value;
    } else if (bulkOps.length >= 1) {
      op_type = "mixed";
    }
    setSaving(true);
    try {
      const res = await adminMarketApply({ op_type, group_label, value, items });
      toast.success(`${res.updated} market ürünü güncellendi`);
      reset();
      setSummaryOpen(false);
      qc.invalidateQueries({ queryKey: ["admin", "market"] });
      qc.invalidateQueries({ queryKey: ["admin", "market-logs"] });
      qc.invalidateQueries({ queryKey: ["market-products"] });
      qc.invalidateQueries({ queryKey: ["market-groups"] });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const showVal = BULK_MODES.find((m) => m.value === bulkMode)?.price;

  return (
    <div data-testid="market-price-manager" className="pb-24">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Market Fiyat Yönetimi</h3>
          <p className="text-sm text-gray-500">
            {products.length} market ürünü · bugün güncellenen:{" "}
            <span className={updatedToday > 0 ? "text-emerald-600 font-medium" : "text-amber-600 font-medium"}>
              {updatedToday}
            </span>
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600" data-testid="market-round-toggle-label">
          <Switch checked={roundOn} onCheckedChange={setRoundOn} data-testid="market-round-toggle" />
          Fiyatları 5₺'ye yuvarla
        </label>
      </div>

      {/* Filtreler */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="market-price-search"
            placeholder="Ürün ara…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisible(PAGE); }}
            className="pl-9"
          />
        </div>
        <Select value={group} onValueChange={(v) => { setGroup(v); setVisible(PAGE); }}>
          <SelectTrigger className="w-[220px]" data-testid="market-price-group">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Gruplar</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Toplu güncelleme paneli */}
      <div data-testid="market-bulk-panel" className="mb-5 rounded-lg border border-[#C57B43]/40 bg-[#C57B43]/[0.06] p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-[#9a5e30]">
          <Layers className="h-4 w-4" /> Toplu Güncelleme
          <span className="font-normal text-gray-500">— filtrelenen <b>{filtered.length}</b> ürüne uygulanır</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={bulkMode} onValueChange={setBulkMode}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="market-bulk-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BULK_MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showVal && (
            <div className="relative w-32">
              {bulkMode.startsWith("pct") ? (
                <Percent className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              ) : (
                <Coins className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              )}
              <Input
                data-testid="market-bulk-value"
                type="number"
                inputMode="decimal"
                placeholder={bulkMode.startsWith("pct") ? "ör. 10" : "ör. 25"}
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                className="pl-8 bg-white"
              />
            </div>
          )}
          <Button
            data-testid="market-bulk-apply"
            onClick={applyBulk}
            disabled={filtered.length === 0}
            className="bg-[#C57B43] hover:bg-[#b06d39]"
          >
            Uygula
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <>
          <p className="mb-2 text-xs text-gray-400" data-testid="market-price-count">
            {filtered.length} ürün · {Math.min(visible, filtered.length)} gösteriliyor
          </p>
          <div className="space-y-2">
            {visibleRows.map((p) => {
              const fresh = istDay(p.price_updated_at) === today;
              const dirty = isDirty(p.slug);
              const passive = statusVal(p.slug) === "passive";
              return (
                <div
                  key={p.slug}
                  data-testid={`market-price-row-${p.slug}`}
                  className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 transition-colors ${
                    dirty ? "border-[#C57B43] bg-[#C57B43]/5"
                      : fresh ? "border-gray-200 bg-white"
                      : "border-gray-200 bg-white"
                  } ${passive ? "opacity-60" : ""}`}
                >
                  <img src={p.image} alt="" className="h-11 w-11 rounded object-cover bg-gray-100 shrink-0" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-800">{p.name}</div>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5">{p.subcategory}</span>
                      {fresh && <><CircleDot className="h-3 w-3 text-emerald-500" /><span>bugün güncel</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="relative">
                      <Input
                        data-testid={`market-price-input-${p.slug}`}
                        type="number"
                        inputMode="decimal"
                        step="1"
                        value={priceVal(p.slug)}
                        onChange={(e) => setPrice(p.slug, e.target.value)}
                        onBlur={() => blurPrice(p.slug)}
                        className="w-24 pr-7 text-right font-mono"
                      />
                      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">₺</span>
                    </div>
                    <span className="w-10 text-xs text-gray-400">/{p.unit || "adet"}</span>
                    <label className="flex items-center gap-1.5 text-[11px]">
                      <Switch
                        data-testid={`market-price-status-${p.slug}`}
                        checked={!passive}
                        onCheckedChange={(on) => setStatus(p.slug, on)}
                      />
                      <span className={passive ? "text-gray-400" : "text-emerald-600"}>
                        {passive ? "Pasif" : "Satışta"}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-400">Ürün bulunamadı.</div>
            )}
          </div>

          {visible < filtered.length && (
            <div className="mt-5 text-center">
              <Button
                data-testid="market-price-load-more"
                variant="outline"
                onClick={() => setVisible((v) => v + PAGE)}
              >
                Daha Fazla Göster ({filtered.length - visible})
              </Button>
            </div>
          )}
        </>
      )}

      {/* Yapışkan kaydet çubuğu */}
      {dirtyList.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-charcoal/10 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <span className="text-sm font-medium text-gray-700" data-testid="market-price-dirty-count">
              {dirtyList.length} üründe değişiklik
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={reset} disabled={saving} data-testid="market-price-discard">
                <RotateCcw className="mr-1.5 h-4 w-4" /> Vazgeç
              </Button>
              <Button
                onClick={() => setSummaryOpen(true)}
                disabled={saving}
                data-testid="market-price-review"
                className="bg-[#C57B43] hover:bg-[#b06d39]"
              >
                <Check className="mr-1.5 h-4 w-4" /> Değişiklikleri Kaydet ({dirtyList.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Değişiklik özeti */}
      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent data-testid="market-summary-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Değişiklik Özeti</DialogTitle>
            <DialogDescription>
              {dirtyList.length} üründe değişiklik kaydedilecek. Lütfen onaylayın.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
            {dirtyList.map((p) => (
              <div key={p.slug} className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2.5 py-1.5 text-sm">
                <span className="min-w-0 flex-1 truncate text-gray-700">{p.name}</span>
                <div className="flex shrink-0 items-center gap-2 font-mono text-xs">
                  {isPriceDirty(p.slug) && (
                    <span>
                      <span className="text-gray-400 line-through">{formatPrice(bySlug[p.slug]?.price)}</span>
                      <span className="mx-1 text-[#C57B43]">→</span>
                      <span className="font-semibold text-[#9a5e30]">{formatPrice(Number(edits[p.slug].price))}</span>
                    </span>
                  )}
                  {isStatusDirty(p.slug) && (
                    <span className={statusVal(p.slug) === "passive" ? "text-gray-400" : "text-emerald-600"}>
                      {statusVal(p.slug) === "passive" ? "Pasif" : "Satışta"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSummaryOpen(false)} disabled={saving} data-testid="market-summary-cancel">Vazgeç</Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="market-summary-confirm"
              className="bg-[#C57B43] hover:bg-[#b06d39]"
            >
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}
              Onayla ve Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
